// Profiles (DESIGN.md § Profiles).
//
// A profile changes **vocabulary, which fields exist, and defaults**. It
// never changes the protocol, and it never unlocks a rung by itself
// except where noted (school transport, where a route is the only object
// that makes sense).
//
// Pure data and lookups. Nothing here reaches a network, a store, or a
// component — the fleet app renders these words and the consumer app
// reads a subset of them off the run.

import { THREAD_MODE, type ThreadMode } from "./protocol.js";

export type ProfileId = "restaurant" | "pharmacy" | "courier" | "school";

/**
 * Every stop field any profile can have. A profile lists the ones it
 * exposes, and a field absent from that list does not exist in the
 * interface — there is no settings page where it comes back.
 */
export type StopFieldId =
  | "orderRef"
  | "bags"
  | "parcels"
  | "waybill"
  | "rxReference"
  | "identityCheck"
  | "coldChain"
  | "note"
  | "instructions"
  | "contact"
  | "stopName"
  | "studentCount";

export type StopFieldKind = "text" | "number" | "flag";

export interface StopFieldDefinition {
  id: StopFieldId;
  kind: StopFieldKind;
  /** The words a person would use, not the wire name. */
  label: string;
  /** Wire field it rides in, when the ticket codec has one (§20.8). */
  wire?: "orderRef" | "parcels" | "instructions" | "contact";
  hint?: string;
}

export interface ProfileWords {
  /** What one unit of work is called. */
  job: string;
  /** Plural of the above, when it is not just +s. */
  jobs: string;
  /** The person waiting at the other end. */
  recipient: string;
  /** The person driving. */
  driver: string;
  /** One place on the plan. */
  stop: string;
  /** What a whole day's sequence is called. */
  run: string;
}

export interface ProfileDefaults {
  /** §11. `coarse` withholds position; `fine` is a live locator. */
  granularity: ThreadMode;
  /** How hard the app pushes a proof photo at a Delivered tap (§20.7). */
  proofPhoto: "off" | "optional" | "encouraged";
  /** The rung from which handover appears at all. */
  handoverFromRung: 2 | 3 | 4;
  /** School: routes are the only object that makes sense, so rung 3 is given. */
  grantsRung: 0 | 3;
}

export interface ProfileDefinition {
  id: ProfileId;
  label: string;
  /** One line the operator reads when the app says which profile it picked. */
  summary: string;
  words: ProfileWords;
  stopFields: StopFieldDefinition[];
  defaults: ProfileDefaults;
  /**
   * Why the granularity default is what it is, in plain words. School is
   * the one default the operator must deliberately override, and DESIGN
   * says the app must say why.
   */
  granularityRationale: string;
}

const FIELD = {
  orderRef: { id: "orderRef", kind: "text", label: "Order number", wire: "orderRef" },
  bags: { id: "bags", kind: "number", label: "Bags", wire: "parcels" },
  parcels: { id: "parcels", kind: "number", label: "Parcels", wire: "parcels" },
  waybill: { id: "waybill", kind: "text", label: "Waybill", wire: "orderRef" },
  rxReference: { id: "rxReference", kind: "text", label: "Prescription reference", wire: "orderRef" },
  identityCheck: { id: "identityCheck", kind: "flag", label: "Check ID at the door" },
  coldChain: { id: "coldChain", kind: "flag", label: "Cold chain" },
  note: {
    id: "note",
    kind: "text",
    label: "Note",
    wire: "instructions",
    hint: "Ring twice, the bell is broken"
  },
  instructions: { id: "instructions", kind: "text", label: "Instructions", wire: "instructions" },
  contact: { id: "contact", kind: "text", label: "Phone", wire: "contact" },
  stopName: { id: "stopName", kind: "text", label: "Stop name" },
  studentCount: { id: "studentCount", kind: "number", label: "Students expected" }
} as const satisfies Record<StopFieldId, StopFieldDefinition>;

export const PROFILES: Readonly<Record<ProfileId, ProfileDefinition>> = Object.freeze({
  restaurant: {
    id: "restaurant",
    label: "Restaurant / takeaway",
    summary: "Orders arrive one at a time and are assigned as riders come free.",
    words: {
      job: "order",
      jobs: "orders",
      recipient: "customer",
      driver: "rider",
      stop: "delivery",
      run: "round"
    },
    stopFields: [FIELD.orderRef, FIELD.bags, FIELD.note, FIELD.contact],
    defaults: {
      granularity: THREAD_MODE.FINE,
      proofPhoto: "off",
      handoverFromRung: 2,
      grantsRung: 0
    },
    granularityRationale:
      "A customer waiting for food wants the dot, and a rider's position on the clock is the product."
  },
  pharmacy: {
    id: "pharmacy",
    label: "Pharmacy",
    summary: "What happened at the door is the record that settles a regulator's question.",
    words: {
      job: "delivery",
      jobs: "deliveries",
      recipient: "recipient",
      driver: "driver",
      stop: "delivery",
      run: "round"
    },
    stopFields: [FIELD.rxReference, FIELD.identityCheck, FIELD.coldChain, FIELD.instructions, FIELD.contact],
    defaults: {
      granularity: THREAD_MODE.FINE,
      proofPhoto: "encouraged",
      handoverFromRung: 2,
      grantsRung: 0
    },
    granularityRationale:
      "A recipient waiting on medication is entitled to the position, and the run is short."
  },
  courier: {
    id: "courier",
    label: "Courier fleet",
    summary: "Planned in the morning, reflowed all day. A skip has to surface the second it happens.",
    words: {
      job: "job",
      jobs: "jobs",
      recipient: "client",
      driver: "driver",
      stop: "drop",
      run: "round"
    },
    stopFields: [FIELD.waybill, FIELD.parcels, FIELD.instructions, FIELD.contact],
    defaults: {
      granularity: THREAD_MODE.FINE,
      proofPhoto: "optional",
      handoverFromRung: 2,
      grantsRung: 0
    },
    granularityRationale:
      "A courier's position is the product, and couriers are consenting adults on the clock."
  },
  school: {
    id: "school",
    label: "School transport",
    summary: "A route is fixed for a term; the driver is not. Parents subscribe once in September.",
    words: {
      job: "run",
      jobs: "runs",
      recipient: "parent",
      driver: "driver",
      stop: "stop",
      run: "route"
    },
    // No parcels. No per-stop contact. Names of children never reach a
    // driver's device — a stop is a place with a number of children at it.
    stopFields: [FIELD.stopName, FIELD.studentCount],
    defaults: {
      granularity: THREAD_MODE.COARSE,
      proofPhoto: "off",
      handoverFromRung: 3,
      grantsRung: 3
    },
    granularityRationale:
      "Parents see the arrival, not the vehicle. A live map of a bus full of other people's "
      + "children is what everyone else sells; a leaked stops-only link is worth about what the "
      + "printed timetable is."
  }
});

export const PROFILE_IDS: readonly ProfileId[] = Object.freeze([
  "restaurant",
  "pharmacy",
  "courier",
  "school"
]);

export function profileFor(id: ProfileId): ProfileDefinition {
  const found = PROFILES[id];
  if (!found) throw new Error(`Unknown profile "${id}".`);
  return found;
}

export function isProfileId(value: unknown): value is ProfileId {
  return typeof value === "string" && (PROFILE_IDS as readonly string[]).includes(value);
}

/** Whether a profile exposes a stop field at all. Absent means absent. */
export function hasStopField(id: ProfileId, field: StopFieldId): boolean {
  return profileFor(id).stopFields.some(definition => definition.id === field);
}

export function stopField(id: ProfileId, field: StopFieldId): StopFieldDefinition | null {
  return profileFor(id).stopFields.find(definition => definition.id === field) ?? null;
}

/** §11's default for this profile, and whether overriding it needs a sentence. */
export function granularityDefault(id: ProfileId): {
  mode: ThreadMode;
  deliberateOverride: boolean;
  rationale: string;
} {
  const profile = profileFor(id);
  return {
    mode: profile.defaults.granularity,
    // School is the one the operator must deliberately override, and the
    // app says why in plain words rather than flipping a switch quietly.
    deliberateOverride: profile.defaults.granularity === THREAD_MODE.COARSE,
    rationale: profile.granularityRationale
  };
}

/** The word this profile uses, so a screen never hardcodes "delivery". */
export function word<K extends keyof ProfileWords>(id: ProfileId, key: K): string {
  return profileFor(id).words[key];
}
